import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, BookMarked, Lock, Radio } from "lucide-react";
import { API, errText } from "@/lib/auth";
import { ErrorBox, Skel } from "@/components/market/Shared";
import { openAI } from "@/components/ai/stream";

const LEVEL_STYLE = { beginner: "text-emerald-300", intermediate: "text-[var(--cyan)]", advanced: "text-[var(--violet)]" };
const REQ = { free: "Free", pro: "Pro", elite: "Elite" };

export const HandbookLibrary = () => {
  const [d, setD] = useState(null);
  useEffect(() => { axios.get(`${API}/handbooks`).then((r) => setD(r.data)).catch(() => setD({ topics: [] })); }, []);
  if (!d) return <Skel h="h-64" />;
  return (
    <section className="space-y-3" data-testid="handbook-library">
      <div className="flex items-end justify-between gap-2">
        <div><h2 className="font-head font-semibold text-xl flex items-center gap-2"><BookMarked className="w-5 h-5 text-[var(--violet)]" />Live handbooks</h2>
          <p className="text-xs text-slate-400 mt-1">Step-by-step guides by experience level, with worked examples from today's real markets.</p></div>
        {d.tier !== "elite" && <Link to="/pro" className="text-xs text-[var(--violet)] font-semibold shrink-0" data-testid="handbooks-upgrade-link">Unlock all</Link>}
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {d.topics.map((t) => (
          <div key={t.id} className="glass p-4" data-testid={`handbook-topic-${t.id}`}>
            <div className="font-head font-semibold">{t.name}</div><div className="text-xs text-slate-500">{t.blurb}</div>
            <div className="mt-3 space-y-1.5">
              {t.levels.map((l) => (
                <Link key={l.id} to={l.locked ? "/pro" : `/learn/${t.id}/${l.id}`} data-testid={`handbook-${t.id}-${l.id}`}
                  className="flex items-center justify-between gap-2 rounded-xl border border-[var(--line)] px-3 py-2 text-sm hover:border-[var(--cyan)] transition-colors">
                  <span className={`font-semibold ${LEVEL_STYLE[l.id]}`}>{l.name}<span className="text-slate-500 font-normal text-xs"> · {l.chapters} chapters</span></span>
                  {l.locked ? <span className="flex items-center gap-1 text-[11px] text-slate-400"><Lock className="w-3 h-3" />{REQ[l.required]}</span> :
                    <span className="text-[11px] text-slate-500">{l.required === "free" ? "Free" : "Included"}</span>}
                </Link>))}
            </div>
          </div>))}
      </div>
    </section>
  );
};

const Example = ({ e }) => (
  <a href={e.url} target="_blank" rel="noreferrer" className="block rounded-xl border border-[var(--line)] p-3 hover:border-[var(--cyan)]" data-testid={`handbook-example-${e.slug}`}>
    <div className="text-sm font-semibold">{e.label}</div>
    <p className="text-xs text-slate-300 mt-1">{e.lesson}</p>
    {e.drill && <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] font-num text-slate-400">
      <div>Fee <b className="text-slate-100">{e.drill.fee_per_contract_cents}¢</b></div><div>Break-even <b className="text-slate-100">{e.drill.breakeven_pct}%</b></div>
      <div>Spread <b className="text-slate-100">{e.drill.spread_cents}¢</b></div><div>1h move <b className="text-slate-100">{e.drill.move_1h_pts == null ? "n/a" : `${e.drill.move_1h_pts}pt`}</b></div>
      <div className="col-span-2 sm:col-span-4 text-amber-200/80">{e.drill.question}</div></div>}
  </a>
);

export default function HandbookReader() {
  const { topic, level } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    setD(null); setErr(null);
    axios.get(`${API}/handbooks/${topic}/${level}`).then((r) => setD(r.data)).catch((e) => setErr({ msg: errText(e), locked: e.response?.status === 402 }));
  }, [topic, level]);
  if (err) return (
    <div className="space-y-3" data-testid="handbook-locked">
      <ErrorBox msg={err.msg} />{err.locked && <Link to="/pro" className="btn-primary" data-testid="handbook-upgrade-btn">See membership plans</Link>}
    </div>);
  if (!d) return <Skel h="h-96" />;
  return (
    <article className="space-y-5 max-w-3xl" data-testid="handbook-reader">
      <Link to="/learn" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-white" data-testid="handbook-back"><ArrowLeft className="w-4 h-4" />All handbooks</Link>
      <div><div className={`text-xs font-bold uppercase tracking-widest ${LEVEL_STYLE[d.level]}`}>{d.level_name}</div>
        <h1 className="font-head font-bold text-3xl tracking-tight" data-testid="handbook-title">{d.topic_name}</h1>
        <button onClick={() => openAI({ context: { handbook_topic: d.topic, handbook_level: d.level } })} className="btn-ghost mt-3 !text-[var(--violet)]" data-testid="handbook-tutor-btn">Ask the AI tutor about this handbook</button></div>
      {d.chapters.map((c, i) => (
        <section key={c.title} className="glass p-5" data-testid={`handbook-chapter-${i}`}>
          <h2 className="font-head font-semibold"><span className="text-slate-500 font-num mr-2">{i + 1}.</span>{c.title}</h2>
          {c.paragraphs.map((p) => <p key={p} className="text-sm text-slate-300 leading-relaxed mt-2">{p}</p>)}
        </section>))}
      <section className="glass p-5 space-y-3" data-testid="handbook-live">
        <h2 className="font-head font-semibold flex items-center gap-2"><Radio className="w-4 h-4 text-emerald-300 pulse-dot" />Live examples</h2>
        {d.live_examples.map((e) => <Example key={e.slug} e={e} />)}
        <p className="text-[11px] text-slate-500">{d.examples_note} Educational only — not financial advice.</p>
      </section>
    </article>
  );
}
